import { useEffect, useState, useCallback } from "react";
import { Bell, X, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * DriverNotifications · v2.6.8 batch O
 * Lightweight in-app inbox surfaced as a banner on the driver dashboard.
 * Polls /api/driver/notifications every 30 s for unread items; collapses
 * to "all caught up" when empty. Driver clicks Dismiss to mark a single
 * notif read, or Mark All to clear the inbox.
 */
export default function DriverNotifications() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/driver/notifications", { params: { only_unread: true, limit: 20 } });
      setItems(data.items || []);
    } catch (e) { /* quiet on transient failures */ }
  }, []);

  useEffect(() => { reload(); const t = setInterval(reload, 30000); return () => clearInterval(t); }, [reload]);

  const dismiss = async (id) => {
    setBusy(id);
    try {
      await api.post(`/driver/notifications/${id}/read`);
      setItems(it => it.filter(n => n.id !== id));
    } catch (e) { toast.error("Could not mark as read"); }
    finally { setBusy(null); }
  };

  const dismissAll = async () => {
    setBusy("all");
    try {
      await api.post("/driver/notifications/read-all");
      setItems([]);
      toast.success("Inbox cleared");
    } catch (e) { toast.error("Could not clear inbox"); }
    finally { setBusy(null); }
  };

  if (items.length === 0) return null;

  // Collapsed banner — shows count + first notification title; click to expand.
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="driver-notif-banner"
        className="w-full mb-4 rounded-lg border border-amber-400/50 bg-amber-400/10 px-4 py-3 flex items-center gap-3 hover:bg-amber-400/15 transition text-left"
      >
        <Bell className="h-5 w-5 text-amber-400 flex-shrink-0 animate-pulse"/>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-amber-300 uppercase tracking-wider">
            {items.length} new {items.length === 1 ? "message" : "messages"} from dispatch
          </div>
          <div className="text-[11px] text-amber-200/80 truncate">{items[0].title}</div>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">View →</span>
      </button>
    );
  }

  // Expanded — list every unread notif.
  return (
    <Card className="bg-zinc-900 border-amber-400/40 p-4 mb-4 space-y-3" data-testid="driver-notif-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-bold text-amber-400 uppercase tracking-wider flex items-center gap-2"><Bell className="h-4 w-4"/>Inbox · {items.length}</div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={dismissAll} disabled={busy === "all"} className="text-[10px] text-zinc-300 hover:text-amber-400" data-testid="notif-mark-all-read">
            {busy === "all" ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : "Mark all read"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)} className="h-7 w-7 p-0" data-testid="notif-collapse"><X className="h-4 w-4"/></Button>
        </div>
      </div>
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {items.map(n => (
          <div key={n.id} className="rounded border border-zinc-800 bg-zinc-950 p-3" data-testid={`notif-${n.id}`}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="text-sm font-semibold text-amber-300">{n.title}</div>
              <Button size="sm" variant="ghost" onClick={() => dismiss(n.id)} disabled={busy === n.id} className="text-[10px] text-zinc-400 hover:text-green-400 h-6" data-testid={`notif-dismiss-${n.id}`}>
                {busy === n.id ? <Loader2 className="h-3 w-3 animate-spin"/> : "Dismiss"}
              </Button>
            </div>
            <p className="text-xs text-zinc-300 whitespace-pre-wrap leading-snug">{n.body}</p>
            <div className="text-[10px] text-zinc-500 mt-2">
              {new Date(n.sent_at).toLocaleString()} · from {n.sent_by}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
